import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Layers, Car } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatPrice(price) {
  if (price == null) return '';
  return price.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * "Kombinierbare Teile" auf der APR-Produktseite.
 * Zeigt andere APR-Teile, die mit diesem Teil TÜV-legal kombinierbar sind
 * (mind. ein gemeinsames Fahrzeug mit gültigem DE-Gutachten).
 */
export default function AprCombinableParts({ alias, brand }) {
  const [data, setData] = useState(null);
  const brandLabel = brand || 'APR';

  useEffect(() => {
    if (!alias) return;
    let active = true;
    fetch(`${API}/api/shop/apr/combinable/${alias}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (active) setData(d); })
      .catch(() => {});
    return () => { active = false; };
  }, [alias]);

  if (!data || !data.applicable || !(data.items || []).length) return null;

  return (
    <section data-testid="apr-combinable-parts" style={{ margin: '36px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <ShieldCheck size={22} color="#22c55e" />
        <h2 style={{ fontSize: 20, color: '#fff', margin: 0 }}>Das passt dazu</h2>
      </div>
      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13.5, margin: '0 0 18px' }}>
        Diese {brandLabel}-Teile passen an mindestens ein gemeinsames Fahrzeug mit diesem Produkt.
        Grün markierte Teile sind nachweislich gemeinsam TÜV-legal; bei den übrigen prüfst du die
        Eintragbarkeit bequem über den Passform-/TÜV-Check im Warenkorb.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
        {data.items.map(it => {
          const legal = !!it.tuev_legal;
          const accent = legal ? 'rgba(34,197,94,0.4)' : 'rgba(245,158,11,0.4)';
          return (
          <Link key={it.alias} to={`/p/${it.slug}`} data-testid={`apr-combinable-item-${it.alias}`}
                style={{ textDecoration: 'none', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column', transition: 'border-color 0.2s, transform 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.transform = 'none'; }}>
            <div style={{ background: '#fff', height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 8 }}>
              <img src={it.image_url ? `${it.image_url.startsWith('/') ? API : ''}${it.image_url}` : '/images/placeholder.svg'}
                   alt={it.name} onError={e => { e.target.src = '/images/placeholder.svg'; }}
                   style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
            </div>
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              {legal ? (
                <span data-testid={`apr-combinable-badge-legal-${it.alias}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start', fontSize: 10.5, fontWeight: 700, color: '#34d399', background: 'rgba(34,197,94,0.12)', borderRadius: 4, padding: '2px 7px' }}>
                  <ShieldCheck size={11} /> TÜV-legal · {it.shared_vehicle_count} Fz.
                </span>
              ) : (
                <span data-testid={`apr-combinable-badge-check-${it.alias}`}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, alignSelf: 'flex-start', fontSize: 10.5, fontWeight: 700, color: '#fbbf24', background: 'rgba(245,158,11,0.12)', borderRadius: 4, padding: '2px 7px' }}>
                  <Car size={11} /> Passform-Check · {it.shared_vehicle_count} Fz.
                </span>
              )}
              <span style={{ color: '#e5e5e5', fontSize: 13, lineHeight: 1.35, flex: 1 }}>{it.name}</span>
              {it.teilegruppe && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#c084fc' }}>
                  <Layers size={11} /> {it.teilegruppe}
                </span>
              )}
              {it.price > 0 && <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{formatPrice(it.price)}</span>}
            </div>
          </Link>
          );
        })}
      </div>
    </section>
  );
}
